import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Dashboard from "@/pages/Dashboard";
import CRM from "@/pages/CRM";
import CRMDashboard from "@/pages/crm/CRMDashboard";
import ContactDetail from "@/pages/ContactDetail";
import ClientWorkspace from "@/pages/ClientWorkspace";
import CaseAnalysis from "@/pages/CaseAnalysis";
import ClientWorkspacePicker from "@/pages/ClientWorkspacePicker";
import Documents from "@/pages/Documents";
import DocumentViewerPage from "@/pages/DocumentViewerPage";
import Estimates from "@/pages/sales/Estimates";
import EstimateDetail from "@/pages/sales/EstimateDetail";
import EstimateWorkspace from "@/pages/sales/EstimateWorkspace";
import EstimatePipeline from "@/pages/sales/EstimatePipeline";
import EstimateFollowUp from "@/pages/sales/EstimateFollowUp";
import EstimateGenerator from "@/pages/sales/EstimateGenerator";
import PublicProposal from "@/pages/PublicProposal";
import PublicEngagement from "@/pages/PublicEngagement";
import PublicEstimate from "@/pages/PublicEstimate";
import WidgetApp from "@/pages/WidgetApp";
import RebookConfirm from "@/pages/RebookConfirm";
import BrandLanding from "@/pages/BrandLanding";
import { PortalAuthProvider } from "@/context/PortalAuthContext";
import { Outlet } from "react-router-dom";
import PortalProtectedRoute from "@/components/portal/PortalProtectedRoute";
import PortalLayout from "@/components/portal/PortalLayout";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalActivate from "@/pages/portal/PortalActivate";
import PortalResetPassword from "@/pages/portal/PortalResetPassword";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalDocuments from "@/pages/portal/PortalDocuments";
import PortalMessages from "@/pages/portal/PortalMessages";
import PortalProfile from "@/pages/portal/PortalProfile";
import PortalTaxProfile from "@/pages/portal/PortalTaxProfile";
import PortalFbarAccounts from "@/pages/portal/PortalFbarAccounts";
import PortalEngagement from "@/pages/portal/PortalEngagement";
import PortalCombinedAcceptSign from "@/pages/portal/PortalCombinedAcceptSign";
import PortalProposal from "@/pages/portal/PortalProposal";
import PortalEstimate from "@/pages/portal/PortalEstimate";
import PortalOnboarding from "@/pages/portal/PortalOnboarding";
import PortalOrganizer from "@/pages/portal/PortalOrganizer";
import PortalStrategicReview from "@/pages/portal/PortalStrategicReview";
import PortalStrategicReviews from "@/pages/portal/PortalStrategicReviews";
import PortalInformationRequests from "@/pages/portal/PortalInformationRequests";
import PortalBilling from "@/pages/portal/PortalBilling";
import PortalScheduling from "@/pages/portal/PortalScheduling";
import Administration from "@/pages/Administration";
import ServiceLibrary from "@/pages/admin/ServiceLibrary";
import HistoricalImport from "@/pages/admin/HistoricalImport";
import AIAgents from "@/pages/admin/AIAgents";
import LeadRecoveryDashboard from "@/pages/admin/LeadRecoveryDashboard";
import UnsubscribeLanding from "@/pages/UnsubscribeLanding";
import KnowledgeCenter from "@/pages/admin/KnowledgeCenter";
import WebChatSettings from "@/pages/admin/WebChatSettings";
import OrganizerMap from "@/pages/admin/OrganizerMap";
import DocumentRules from "@/pages/admin/DocumentRules";
import OperationsAdminTools from "@/pages/admin/OperationsCenter";
import OperationsCenterPage from "@/pages/OperationsCenterPage";
import AIOperations from "@/pages/admin/AIOperations";
import WorkflowHarness from "@/pages/admin/WorkflowHarness";
import LetterCertification from "@/pages/admin/LetterCertification";
import PracticeProfile from "@/pages/admin/PracticeProfile";
import DocumentTaxonomy from "@/pages/admin/DocumentTaxonomy";
import SchedulingDashboard from "@/pages/scheduling/SchedulingDashboard";
import SchedulingCalendar from "@/pages/scheduling/SchedulingCalendar";
import SchedulingAppointments from "@/pages/scheduling/SchedulingAppointments";
import MeetingTypeLibrary from "@/pages/scheduling/MeetingTypeLibrary";
import StaffAvailability from "@/pages/scheduling/StaffAvailability";
import SchedulingSettings from "@/pages/scheduling/SchedulingSettings";
import SettingsPage from "@/pages/Settings";
import ModulePlaceholder from "@/pages/ModulePlaceholder";
import ClientSuccess from "@/pages/ClientSuccess";
import StaffOrganizers from "@/pages/cs/StaffOrganizers";
import StaffOrganizer from "@/pages/cs/StaffOrganizer";
import OrganizerTemplates from "@/pages/cs/OrganizerTemplates";
import StrategicReview from "@/pages/cs/StrategicReview";
import StrategicReviewPreview from "@/pages/cs/StrategicReviewPreview";
import LeadWorkflow from "@/pages/workflow/LeadWorkflow";
import ClientWorkflow from "@/pages/workflow/ClientWorkflow";
import ProductionWorkflow from "@/pages/workflow/ProductionWorkflow";
import ReviewQueue from "@/pages/ops/ReviewQueue";
import OrganizerReviewQueue from "@/pages/ops/OrganizerReviewQueue";
import Production from "@/pages/production/Production";
import ProductionWorkspace from "@/pages/production/ProductionWorkspace";
import FinanceDashboard from "@/pages/finance/FinanceDashboard";
import Invoices from "@/pages/finance/Invoices";
import InvoiceDetail from "@/pages/finance/InvoiceDetail";
import Payments from "@/pages/finance/Payments";
import AccountsReceivable from "@/pages/finance/AccountsReceivable";
import RevenuePipeline from "@/pages/finance/RevenuePipeline";
import PaymentReports from "@/pages/finance/PaymentReports";
import PaymentMethodsConfig from "@/pages/finance/PaymentMethodsConfig";
import UnifiedInbox from "@/pages/comms/UnifiedInbox";
import CommunicationsCenter from "@/pages/comms/CommunicationsCenter";
import CommsDashboard from "@/pages/comms/CommsDashboard";
import CommTemplates from "@/pages/comms/CommTemplates";
import Templates from "@/pages/templates/Templates";
import CommSettings, { UnansweredQueue } from "@/pages/comms/CommSettings";
import RevisionInbox from "@/pages/comms/RevisionInbox";
import ExecutiveDashboard from "@/pages/exec/ExecutiveDashboard";
import HomeLanding from "@/pages/HomeLanding";
import FirmOverview from "@/pages/FirmOverview";
import LiveOperations from "@/pages/LiveOperations";
import MeetingReview from "@/pages/MeetingReview";
import ConsultationSummaryReview from "@/pages/ConsultationSummaryReview";

/**
 * SyntheticErrorTestPage — diagnostic component for AppErrorBoundary verification.
 * Preview/certification use only. Not linked from any navigation.
 * Throws a controlled render error when mounted so the boundary can be exercised
 * without touching any real client data or Production records.
 */
function SyntheticErrorTestPage() {
  throw new Error(
    "[SyntheticErrorTestPage] Controlled render error for AppErrorBoundary certification. " +
    "This is expected during Preview testing and confirms the boundary is active."
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppErrorBoundary>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/p/:token" element={<PublicProposal />} />
            <Route path="/e/:token" element={<PublicEngagement />} />
            <Route path="/s/:token" element={<PublicEstimate />} />
            <Route path="/widget" element={<WidgetApp />} />
            <Route path="/rebook/:token" element={<RebookConfirm />} />
            <Route path="/c/:firmId" element={<BrandLanding />} />
            <Route path="/c" element={<BrandLanding />} />
            <Route path="/unsubscribe/:token" element={<UnsubscribeLanding />} />

            {/* Build 009A: Secure Client Portal (separate portal auth context) */}
            <Route element={<PortalAuthProvider><Outlet /></PortalAuthProvider>}>
              <Route path="/portal/login" element={<PortalLogin />} />
              <Route path="/portal/activate/:token" element={<PortalActivate />} />
              <Route path="/portal/reset-password" element={<PortalResetPassword />} />
              <Route element={<PortalProtectedRoute><PortalLayout /></PortalProtectedRoute>}>
                <Route path="/portal" element={<PortalDashboard />} />
                <Route path="/portal/documents" element={<PortalDocuments />} />
                <Route path="/portal/messages" element={<PortalMessages />} />
                <Route path="/portal/engagements/:id" element={<PortalEngagement />} />
                <Route path="/portal/accept-and-sign/:id" element={<PortalCombinedAcceptSign />} />
                <Route path="/portal/proposals/:id" element={<PortalProposal />} />
                <Route path="/portal/estimates/:estimateId" element={<PortalEstimate />} />
                <Route path="/portal/onboarding" element={<PortalOnboarding />} />
                <Route path="/portal/organizer" element={<PortalOrganizer />} />
                <Route path="/portal/strategic-review" element={<PortalStrategicReview />} />
                <Route path="/portal/strategic-reviews" element={<PortalStrategicReviews />} />
                <Route path="/portal/requests" element={<PortalInformationRequests />} />
                <Route path="/portal/billing" element={<PortalBilling />} />
                <Route path="/portal/scheduling" element={<PortalScheduling />} />
                <Route path="/portal/profile" element={<PortalProfile />} />
                <Route path="/portal/tax-profile" element={<PortalTaxProfile />} />
                <Route path="/portal/fbar/:engagementId/:year" element={<PortalFbarAccounts />} />
                {/* Defense-in-depth: any unrecognised /portal/* URL must NEVER fall through to staff routes.
                    Redirect to portal dashboard — this prevents the catch-all at the bottom from sending
                    a portal session into the staff application. */}
                <Route path="/portal/*" element={<Navigate to="/portal" replace />} />
              </Route>
            </Route>

            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<OperationsCenterPage />} />
              <Route path="/live-ops" element={<OperationsCenterPage />} />
              <Route path="/overview" element={<FirmOverview />} />
              <Route path="/crm" element={<CRMDashboard />} />
              <Route path="/crm/leads" element={<CRM scope="leads" />} />
              <Route path="/crm/active-clients" element={<CRM scope="active-clients" />} />
              <Route path="/crm/inactive-clients" element={<CRM scope="inactive-clients" />} />
              <Route path="/crm/:id" element={<ContactDetail />} />
              <Route path="/client-workspace" element={<ClientWorkspacePicker />} />
              <Route path="/client-workspace/:id" element={<ClientWorkspace />} />
              <Route path="/case-analysis/:clientId" element={<CaseAnalysis />} />
              <Route path="/meeting-review/:transcriptId" element={<MeetingReview />} />
              <Route path="/consultation-summary/:summaryId" element={<ConsultationSummaryReview />} />
              <Route path="/lifecycle" element={<Navigate to="/overview" replace />} />
              <Route path="/sales" element={<Navigate to="/sales/board" replace />} />
              <Route path="/sales/board" element={<LeadWorkflow />} />
              <Route path="/sales/estimates" element={<Estimates />} />
              <Route path="/sales/estimates/new" element={<Navigate to="/sales/estimates?create=1" replace />} />
              <Route path="/sales/estimates/:id" element={<EstimateWorkspace />} />
              <Route path="/sales/estimate-generator" element={<EstimateGenerator />} />
              <Route path="/sales/proposals" element={<Navigate to="/sales/estimates" replace />} />
              <Route path="/sales/proposals/:id" element={<EstimateDetail />} />
              <Route path="/sales/pipeline" element={<EstimatePipeline />} />
              <Route path="/sales/templates" element={<Navigate to="/templates?tab=estimate" replace />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/sales/follow-up" element={<EstimateFollowUp />} />
              <Route path="/client-success" element={<ClientWorkflow />} />
              <Route path="/client-success/classic" element={<ClientSuccess />} />
              <Route path="/client-success/organizers" element={<StaffOrganizers />} />
              <Route path="/client-success/organizers/:oid" element={<StaffOrganizer />} />
              <Route path="/client-success/strategic-review/:clientId" element={<StrategicReview />} />
              <Route path="/client-success/strategic-review/:clientId/preview/:aid" element={<StrategicReviewPreview />} />
              <Route path="/organizer-templates" element={<OrganizerTemplates />} />
              <Route path="/ops/review-queue" element={<ReviewQueue />} />
              <Route path="/ops/organizer-review" element={<OrganizerReviewQueue />} />
              <Route path="/production" element={<ProductionWorkflow />} />
              <Route path="/production/classic" element={<Production />} />
              <Route path="/production/queue" element={<Navigate to="/production/classic?tab=queue" replace />} />
              <Route path="/production/:pid" element={<ProductionWorkspace />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/documents/:documentId" element={<DocumentViewerPage />} />
              <Route path="/workspace/:clientId/documents/:documentId" element={<DocumentViewerPage />} />
              <Route path="/finance" element={<FinanceDashboard />} />
              <Route path="/finance/invoices" element={<Invoices />} />
              <Route path="/finance/invoices/:id" element={<InvoiceDetail />} />
              <Route path="/finance/payments" element={<Payments />} />
              <Route path="/finance/receivables" element={<AccountsReceivable />} />
              <Route path="/finance/pipeline" element={<RevenuePipeline />} />
              <Route path="/finance/payment-reports" element={<PaymentReports />} />
              <Route path="/finance/payment-methods" element={<PaymentMethodsConfig />} />
              <Route path="/communications" element={<CommsDashboard />} />
              <Route path="/communications/center" element={<CommunicationsCenter />} />
              {/* Legacy routes redirect to unified center */}
              <Route path="/communications/inbox" element={<Navigate to="/communications/center" replace />} />
              <Route path="/communications/leads" element={<Navigate to="/communications/center" replace />} />
              <Route path="/communications/clients" element={<Navigate to="/communications/center" replace />} />
              <Route path="/communications/client" element={<Navigate to="/communications/center" replace />} />
              <Route path="/communications/mine" element={<Navigate to="/communications/center" replace />} />
              <Route path="/communications/team" element={<Navigate to="/communications/center" replace />} />
              <Route path="/communications/unassigned" element={<Navigate to="/communications/center" replace />} />
              <Route path="/communications/penny" element={<UnifiedInbox view="penny" />} />
              <Route path="/communications/internal-notes" element={<UnifiedInbox view="internal-notes" />} />
              <Route path="/communications/templates" element={<Navigate to="/templates?tab=email" replace />} />
              <Route path="/communications/settings" element={<CommSettings />} />
              <Route path="/communications/unanswered" element={<UnansweredQueue />} />
              <Route path="/communications/revision-requests" element={<RevisionInbox />} />
              <Route path="/executive" element={<ExecutiveDashboard />} />
              <Route path="/reporting" element={<ModulePlaceholder moduleKey="reporting" />} />
              <Route path="/administration" element={<Administration />} />
              <Route path="/administration/practice-profile" element={<PracticeProfile />} />
              <Route path="/administration/document-taxonomy" element={<DocumentTaxonomy />} />
              <Route path="/service-library" element={<ServiceLibrary />} />
              <Route path="/historical-import" element={<HistoricalImport />} />
              <Route path="/admin/letter-certification" element={<LetterCertification />} />
              {/* AI Center — canonical routes */}
              <Route path="/ai" element={<Navigate to="/ai/agents" replace />} />
              <Route path="/ai/agents" element={<AIAgents />} />
              <Route path="/ai/knowledge" element={<KnowledgeCenter />} />
              <Route path="/ai/operations" element={<AIOperations />} />
              <Route path="/ai/workflow-testing" element={<WorkflowHarness />} />
              <Route path="/ai/lead-recovery" element={<LeadRecoveryDashboard />} />
              {/* Legacy AI routes → redirect to AI Center */}
              <Route path="/ai-agents" element={<Navigate to="/ai/agents" replace />} />
              <Route path="/knowledge-center" element={<Navigate to="/ai/knowledge" replace />} />
              <Route path="/webchat-widget" element={<WebChatSettings />} />
              <Route path="/organizer-map" element={<OrganizerMap />} />
              <Route path="/document-rules" element={<DocumentRules />} />
              <Route path="/operations-center" element={<OperationsCenterPage />} />
              <Route path="/admin/ops-tools" element={<OperationsAdminTools />} />
              <Route path="/ai-operations" element={<Navigate to="/ai/operations" replace />} />
              <Route path="/workflow-harness" element={<Navigate to="/ai/workflow-testing" replace />} />
              <Route path="/scheduling" element={<SchedulingDashboard />} />
              <Route path="/scheduling/calendar" element={<SchedulingCalendar />} />
              <Route path="/scheduling/appointments" element={<SchedulingAppointments />} />
              <Route path="/scheduling/meeting-types" element={<MeetingTypeLibrary />} />
              <Route path="/scheduling/availability" element={<StaffAvailability />} />
              <Route path="/scheduling/settings" element={<SchedulingSettings />} />
              <Route path="/settings" element={<SettingsPage />} />
              {/* ── Diagnostic: ErrorBoundary synthetic test — Preview only ─────── */}
              <Route path="/test-error-boundary" element={<SyntheticErrorTestPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
        </AppErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;
